"use client";

import { Content } from "@/services/content/types";
import { createImageSizes, createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import AiLabel, { getAiLabelText } from "../ai-label";
import Category from "../category";
import Icon from "../icon";
import TeaserHeading from "../teaser-heading";

type HeroProps = {
  title?: string;
  mainCategory?: Content.Category;
  imageCaption?: string;
  images?: {
    url: string;
    alt?: string;
    title?: any;
    photographer?: string;
    asset?: Content.Asset;
  }[];
};

const isSvg = (url: string) => /\.svg(\?|$)/i.test(url);
const isGif = (url: string) => /\.gif(\?|$)/i.test(url);

const PauseButton = ({
  isPaused,
  onClick,
}: {
  isPaused: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="btn absolute top-4 right-4 sm:top-auto sm:bottom-4 z-20 bg-static-black/80 hover:bg-static-black text-static-white gap-2"
  >
    {isPaused ? (
      <Icon.PlayInverted className="fill-static-white w-5 h-5" />
    ) : (
      <Icon.PauseInverted className="fill-static-white w-5 h-5" />
    )}
    {isPaused ? "Spela upp animation" : "Pausa animation"}
  </button>
);

// object-fit doesn't reliably apply to an inline <svg> root (only to
// replaced elements like <img>/<video>), so to get "cover" behavior we set
// the SVG's own preserveAspectRatio instead — its native equivalent.
const forceCoverFit = (svg: string) => {
  const rootTagEnd = svg.indexOf(">") + 1;
  const rootTag = svg.slice(0, rootTagEnd);

  if (/preserveAspectRatio\s*=/.test(rootTag)) {
    return (
      rootTag.replace(
        /preserveAspectRatio\s*=\s*"[^"]*"/,
        `preserveAspectRatio="xMidYMid slice"`,
      ) + svg.slice(rootTagEnd)
    );
  }

  return svg.replace(/<svg\b/, `<svg preserveAspectRatio="xMidYMid slice"`);
};

// SVGs uploaded as the hero image can contain their own looping CSS
// animations. next/image always rasterizes to a static format, so for SVGs
// we fetch the raw markup (via the same-origin image proxy, since the CMS
// host doesn't send CORS headers) and inline it, which keeps the animation
// alive — and lets us add a pause control for it (WCAG 2.2.2).
const AnimatedSvgHero = ({ url, alt }: { url: string; alt: string }) => {
  const [markup, setMarkup] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = process.env.NEXT_PUBLIC_IMAGE_PROXY_TOKEN || "";
    const proxyUrl = `/api/image-proxy?token=${encodeURIComponent(token)}&url=${encodeURIComponent(url)}`;

    let cancelled = false;

    fetch(proxyUrl)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((svg) => {
        if (!cancelled && svg.includes("<svg")) setMarkup(forceCoverFit(svg));
      })
      .catch(() => {
        // Fall through to the static <Image> fallback below.
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // The animation-play-state CSS toggle below only stops CSS @keyframes
  // animations. Some SVG exports instead animate natively via SMIL
  // elements (<animate>, <animateTransform>, <animateMotion>...), which
  // CSS has no power over — those are paused/resumed through the SVG
  // DOM's own timeline controls instead. Doing both covers either
  // technique, since a single asset could in principle use both.
  useEffect(() => {
    const svgEl = containerRef.current?.querySelector("svg") as
      | (SVGSVGElement & {
          pauseAnimations?: () => void;
          unpauseAnimations?: () => void;
        })
      | null;
    if (!svgEl) return;

    if (isPaused) {
      svgEl.pauseAnimations?.();
    } else {
      svgEl.unpauseAnimations?.();
    }
  }, [isPaused, markup]);

  if (!markup) {
    return (
      <div className="w-full h-full absolute top-0 z-0">
        <Image
          fill
          style={{ objectFit: "cover", objectPosition: "center center" }}
          alt={alt}
          sizes={createImageSizes({
            mobile: "100vw",
            tablet: "100vw",
            desktop: "100vw",
          })}
          quality={90}
          src={createImageUrl(url, { crop: "16:9" })}
        />
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        role="img"
        aria-label={alt}
        className={classNames(
          "absolute inset-0 z-0 overflow-hidden [&_svg]:h-full [&_svg]:w-full",
          isPaused && "[&_*]:![animation-play-state:paused]",
        )}
        dangerouslySetInnerHTML={{ __html: markup }}
      />
      <PauseButton
        isPaused={isPaused}
        onClick={() => setIsPaused((paused) => !paused)}
      />
    </>
  );
};

// Animated GIFs can't be paused via CSS like an SVG's animation can — the
// motion is baked into the file's own frame timing, entirely outside the
// browser's control. So instead of pausing in place, the pause control
// here swaps the image between the frozen single-frame version (see
// createImageUrl's GIF handling) and the live animated one. Resuming
// restarts playback from the first frame rather than where it left off,
// but WCAG 2.2.2 only requires a way to stop the motion, not a seamless
// resume.
const AnimatedGifHero = ({ url, alt }: { url: string; alt: string }) => {
  const [isPaused, setIsPaused] = useState(false);

  return (
    <>
      <div className="w-full h-full absolute top-0 z-0">
        <Image
          fill
          style={{ objectFit: "cover", objectPosition: "center center" }}
          alt={alt}
          sizes={createImageSizes({
            mobile: "100vw",
            tablet: "100vw",
            desktop: "100vw",
          })}
          quality={90}
          src={createImageUrl(url, { crop: "16:9", allowAnimation: !isPaused })}
        />
      </div>
      <PauseButton
        isPaused={isPaused}
        onClick={() => setIsPaused((paused) => !paused)}
      />
    </>
  );
};

const StoryImage = ({
  title,
  mainCategory,
  images,
  imageCaption,
}: HeroProps) => {
  if (!images) return null;

  // At high zoom the hero shrinks to the height of its text, so the AI label
  // and the mobile pause button would sit on top of the heading. Reserve room
  // above the text for them (WCAG 1.4.10 Reflow).
  const hasTopOverlay =
    Boolean(getAiLabelText(images[0].asset)) ||
    isSvg(images[0].url) ||
    isGif(images[0].url);

  return (
    <div className="w-full min-h-[calc(100dvh-260px)] md:min-h-[calc(100dvh-250px)] relative -mt-8 mb-8 main-image-area flex flex-col justify-end">
      {isSvg(images[0].url) ? (
        <AnimatedSvgHero url={images[0].url} alt={images[0].alt || ""} />
      ) : isGif(images[0].url) ? (
        <AnimatedGifHero url={images[0].url} alt={images[0].alt || ""} />
      ) : (
        <div className="w-full h-full absolute top-0 z-0">
          <Image
            fill
            style={{
              objectFit: "cover",
              objectPosition: "center center",
            }}
            alt={images[0].alt || ""}
            sizes={createImageSizes({
              mobile: "100vw",
              tablet: "100vw",
              desktop: "100vw",
            })}
            quality={90}
            className="cursor-pointer"
            src={createImageUrl(images[0].url, { crop: "16:9" })}
            // TODO: Quick fix to prevent adding "2500" device size to all images
            loader={({ src, width, quality }) => {
              const url = new URL(src);

              url.searchParams.set("auto", "webp");

              // If width is maxed at 960, override it for hero images only
              if (width === 960) {
                url.searchParams.set("width", "2500");
              } else {
                url.searchParams.set("width", width.toString());
              }

              url.searchParams.set("quality", (quality || 90).toString());

              return url.href;
            }}
          />
        </div>
      )}
      <AiLabel image={images[0].asset} className="z-20 [&>span]:top-12" />
      <div className="bg-gradient-to-t from-static-black to-transparent absolute inset-x-0 bottom-0 h-[90%] z-10"></div>
      <div
        className={classNames(
          "relative z-10 container space-y-4 lg:space-y-8 lg:max-w-[960px] lg:px-24 mx-auto pb-4",
          hasTopOverlay && "pt-20",
        )}
      >
        {mainCategory && (
          <Category category={mainCategory} showFollowToggle isLink inverted />
        )}
        {title && (
          <TeaserHeading
            as="h1"
            text={title}
            className="font-bold text-4xl md:text-5xl lg:text-6xl text-static-white !mt-2"
          />
        )}
        {imageCaption && (
          <p className="md:text-xl text-static-white">{imageCaption}</p>
        )}
      </div>
    </div>
  );
};

export default StoryImage;
